import React, { useEffect, useState } from 'react';
import { addDays, format, getDay, isBefore, parse, startOfDay, parseISO, endOfMonth } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { useQuery } from '@apollo/client';
import { useCalendar } from '../calendarContext';
import { BookingTypeStatus, useIsMobile } from '../../../../util/utils';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import { GET_BOOKING_BY_BOOKING_TYPE_ID, GET_STAFF_WORKING_HOUR } from '../../../../graphql/queries';
import dayjs from 'dayjs';

const BookingTimeSlotWrapper = ({ children, isTimeSlotVisible = false, bookingType, selectedStaffId = null, userTimezone, viewType, businessUrl }) => {
    const { selectedDate, setSelectedDate } = useCalendar();
    const timeZone = userTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    // Step 1: Treat `selectedDate` as local time
    const localStartDate = startOfDay(selectedDate);

    // Step 2: Calculate the start of the week (Sunday) in local time
    const currentDayOfWeek = getDay(localStartDate);
    const weekStartDate = addDays(localStartDate, -currentDayOfWeek);

    // Extract staff IDs from the business
    const staffIds = bookingType?.assignedStaffs?.map((staff) => staff.staffId) || [];
    const startDate = viewType === "week" ? startOfDay(weekStartDate) : localStartDate
    // send utc date

    // Use the formatted UTC start date in the query
    const { data: availability, loading, error, refetch } = useQuery(GET_STAFF_WORKING_HOUR, {
        variables: {
            staffIds: selectedStaffId === "All" || !selectedStaffId ? staffIds : [selectedStaffId],
            bookingTypeId: bookingType?.id || "1",
            startDate: dayjs(startDate).format('YYYY-MM-DD'), // Pass local
            viewType: viewType,
            timezone: userTimezone
        },
        skip: staffIds.length === 0,
    });


    const { data: bookingData, loading: bookingLoading, error: bookingError, refetch: bookingReferesh } = useQuery(GET_BOOKING_BY_BOOKING_TYPE_ID, {
        variables: {
            bookingTypeId: Number(bookingType?.id),
        },
        skip: !bookingType?.id,
    });

    const [workingHoursData, setWorkingHoursData] = useState(availability);
    const openings = React.useMemo(() => getOpeningsForMonth(), [workingHoursData, selectedDate])

    useEffect(() => {
        if (availability) {
            setWorkingHoursData(availability);
        }
    }, [availability, startDate]);
    if (loading || bookingLoading) return <p>Loading...</p>;
    if (error || bookingError) return <p>Error :</p>;



    const weekEndDate = addDays(weekStartDate, 6);

    const daysOfWeek = [];
    let currentDatePointer = new Date(weekStartDate);

    const RenderButtons = ({ slots, handleTimeSlotClick, day }) => {

        return slots.map((slot) => createTimeSlotButton(slot, handleTimeSlotClick, day));
    };

    const NoSlotsMessage = ({ message = "No slots available.", style = {} }) => {
        return (
            <p
                style={{
                    color: "#d32f2f", // Use a red color for better visibility
                    fontSize: ".48rem",  // Slightly larger font for readability
                    textAlign: "center",
                    margin: "2px 0",  // Increased margin for better spacing
                    fontWeight: "600", // Slightly bolder text for emphasis
                    lineHeight: "1.5", // Increase line height for better readability
                    ...style, // Allow custom styles to be passed in
                }}
            >
                {message}
            </p>
        );
    };

    const createTimeSlotButton = (slot, handleTimeSlotClick, day) => {
        const isDisabled = isSlotDisabled(slot.time, day, timeZone);
        const isSlotAvailable = slot.remainingSlots > 0;
        const { remainingSlots, showRemaining } = slot;
        const isAssigneStaffAvaiable = slot.assignedStaffs?.length > 0
        return (
            <Button
                key={slot.time}
                color="primary"
                sx={{
                    height: "auto",
                    fontSize: "0.75rem",
                    borderRadius: "4px",
                    backgroundColor: isDisabled || !isSlotAvailable ? "#E0E0E0" : "#E0F7FA",
                    color: isDisabled || !isSlotAvailable ? "#BDBDBD" : "#00796B",
                    pointerEvents: isDisabled || !isSlotAvailable ? "none" : "auto",
                    opacity: isDisabled || !isSlotAvailable ? 0.6 : 1,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "auto",
                    padding: "4px 6px",
                }}
                disabled={isDisabled || !isSlotAvailable}
                onClick={() => handleTimeSlotClick(slot.time, day)}
            >
                <Typography variant="body1" sx={{ marginBottom: '2px', fontSize: "0.75rem" }}>
                    {slot.time} {/* Always show slot time */}
                </Typography>

                {showRemaining && !isDisabled && isSlotAvailable && (
                    <Typography
                        sx={{
                            fontSize: "0.6rem",
                            color: "#00796B", // Consistent color with the button
                            marginTop: "1px",
                            textTransform: 'lowercase',
                        }}
                    >
                        Available: {remainingSlots} {/* Only show remaining slots if showRemaining is true */}
                    </Typography>
                )}

                {/* If showRemaining is true, display "No slots available" only if remainingSlots is 0 */}
                {remainingSlots === 0 && !isAssigneStaffAvaiable && !isDisabled && (
                    <NoSlotsMessage />
                )}
            </Button>

        );
    };

    const getDayAbbreviation = (date) => format(date, "EEE").toUpperCase();
    while (currentDatePointer <= weekEndDate) {
        daysOfWeek.push({
            date: new Date(currentDatePointer),
            abbreviation: getDayAbbreviation(currentDatePointer),
        });
        currentDatePointer = addDays(currentDatePointer, 1);
    }

    const convertTimeToUserTimezone = (utcTime, userTimeZone) => {
        try { 
            const utcDate = parseISO(utcTime);
 
            const userDate = utcToZonedTime(utcDate, userTimeZone); 

            // 5) Format the final time (12-hour clock with AM/PM)
            return format(userDate, 'h:mma', { timeZone: userTimeZone }).toLowerCase();
        } catch (error) {
            console.error('Error converting timezone:', error);
            return utcTime; // fallback to raw UTC string if conversion fails
        }
    }

    function getOpeningsForMonth() {
        // If workingHoursData isn't available, return an empty array.
        if (!workingHoursData) return [];

        // Use selectedDate as the reference for the month.
        const baseDate = new Date(selectedDate);
        // Normalize system today to start of day.
        const systemToday = startOfDay(new Date());

        // Determine the start of our loop:
        // If the selected month/year is the current month/year, start from systemToday.
        // Otherwise, start from the first day of the month.
        let startLoop;
        if (
            baseDate.getMonth() === systemToday.getMonth() &&
            baseDate.getFullYear() === systemToday.getFullYear()
        ) {
            startLoop = systemToday;
        } else {
            startLoop = startOfDay(new Date(baseDate.getFullYear(), baseDate.getMonth(), 1));
        }

        // Get the last day of the selected month.
        const monthEnd = endOfMonth(baseDate);
        const results = [];
        let day = startLoop;

        // Loop from the startLoop date until the end of the month (inclusive).
        while (isBefore(day, addDays(monthEnd, 1))) {
            const formattedDate = format(day, 'yyyy-MM-dd');

            // Find the availability entry for the formatted date.
            const dayAvailability = workingHoursData.getStaffAvailability?.availability?.find(
                (entry) => entry.date === formattedDate
            );

            // Mark the day as "yes" if available, otherwise "no".
            const availableMark = dayAvailability?.timeSlots.length > 0 ? "yes" : "no";

            results.push({
                date: formattedDate,
                available: availableMark
            });

            // Move on to the next day.
            day = addDays(day, 1);
        }

        return results;
    }


    const getTimeSlotsForDate = (date) => {
        if (!workingHoursData?.getStaffAvailability?.availability) return [];
    
        const userTimeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    
        // Format date in user's timezone, not just in UTC
        const formattedDate = format(date, 'yyyy-MM-dd');
    
        // Match the availability for the local date
        const dayAvailability = workingHoursData?.getStaffAvailability?.availability
            .find((day) => day.date === formattedDate);
    
        if (!dayAvailability) return [];
    
        return dayAvailability.timeSlots.map((slot) => {
            // Convert time to user's timezone (for display)
            const convertedTime = convertTimeToUserTimezone(slot.time, userTimeZone);
    
            // Convert for normalized comparison (safe for filtering)
            const normalizedSlotTime = convertToUTC(slot.time); // assumed to produce something like '2025-04-22T23:30:00.000Z'
    
            const bookedBookingTypes = bookingData?.getBookingByBookingTypeId;
    
            const bookedSlots = bookedBookingTypes?.filter((booking) => {
                if (booking.status === BookingTypeStatus.DELETED) return false;
    
                const bookedTime = convertToUTC(booking.time);
                return bookedTime === normalizedSlotTime;
            });
    
            const showRemaining = bookingType?.showRemaining ?? false;
            const bookedCount = bookedSlots?.length || 0;
            const maxInvitees = bookingType?.maxInvitees || 0;
            const remainingSlots = maxInvitees - bookedCount;
    
            return {
                time: convertedTime,
                availableStaff: slot.availableStaff,
                remainingSlots,
                showRemaining
            };
        });
    };
    
    // like utcToZonedTime  will adjust for you no need to do it yourself
    // function isDaylightSavingTime(localDate, timeZone) {
    //     // We'll compare offsets for Jan 1 and Jul 1 in the same year
    //     const january = utcToZonedTime(
    //         new Date(localDate.getFullYear(), 0, 1),
    //         timeZone
    //     );
    //     const july = utcToZonedTime(
    //         new Date(localDate.getFullYear(), 6, 1),
    //         timeZone
    //     );

    //     // Get the UTC offsets (in minutes) for January 1, July 1, and the current date
    //     const januaryOffset = january.getTimezoneOffset();
    //     const julyOffset = july.getTimezoneOffset();
    //     const currentOffset = localDate.getTimezoneOffset();

    //     // Typically, the "smaller" offset is the DST offset in the Northern Hemisphere
    //     // (the clock moves forward, so you get a smaller *offset* from UTC).
    //     const smallerOffset = Math.min(januaryOffset, julyOffset);

    //     // If currentOffset matches the smaller offset, it's probably DST
    //     return currentOffset === smallerOffset;
    // }

    const convertToUTC = (time) => {
        // Convert the local time (booking.time) to a Date object and then to ISO format in UTC
        const date = new Date(time);
        return date.toISOString(); // Returns in ISO 8601 format (UTC)
    };

    const handleTimeSlotClick = (time, date) => {


        // Get available staff for this time slot
        const daySlots = getTimeSlotsForDate(date);
        const timeSlot = daySlots.find((slot) => slot.time === time);

        if (!timeSlot?.availableStaff?.length) {
            console.error("No staff available for this time slot");
            return;
        }

        const bookingDetails = {
            businessUrl: businessUrl,
            bookingTypeLink: bookingType.link,
            staffUrl: bookingType?.assignedStaffs.find((staff) => staff.staffId === selectedStaffId)?.url,
            time: `${format(date, "yyyy-MM-dd")}T${time}`, // Construct full time
        };
        localStorage.setItem("bookingDetails", JSON.stringify(bookingDetails));
        localStorage.setItem("timezone", timeZone?.timezone ?? timeZone);
        // Navigate to the next page
        const path = `/${businessUrl}/${bookingDetails.staffUrl}/${bookingDetails.bookingTypeLink}/${bookingDetails.time}`;
        navigate(path);
    };
    ;
    const isSlotDisabled = (slotTime, slotDate, timeZone) => {
        // Ensure slotDate is a Date object
        const slotDateObject = slotDate instanceof Date ? slotDate : slotDate?.date;

        // Parse the slot time into hours and minutes
        const parsedTime = parse(slotTime, 'h:mma', new Date());
        if (isNaN(parsedTime)) {
            console.error('Invalid slot time:', slotTime);
            return true; // Disable if the slot time is invalid
        }

        const hour = parsedTime.getHours();
        const minute = parsedTime.getMinutes();

        // Combine slot date and time into a Date object in the specified time zone
        const slotDateTime = new Date(
            slotDateObject.getFullYear(),
            slotDateObject.getMonth(),
            slotDateObject.getDate(),
            hour,
            minute
        );
        // Convert slotDateTime to UTC based on the specified time zone
        const slotDateTimeUtc = zonedTimeToUtc(slotDateTime, timeZone);

        // Get the current date and time in UTC
        const currentDateTimeUtc = new Date();

        // Compare dates
        return slotDateTimeUtc <= currentDateTimeUtc;

    };

    return React.cloneElement(children, {
        isMobile,
        workingHoursData,
        setSelectedDate,
        timeZone,
        userTimezone,
        isSlotDisabled,
        handleTimeSlotClick,
        isTimeSlotVisible,
        getTimeSlotsForDate,
        viewType,
        RenderButtons,
        bookingType,
        openings
    });
};

export default BookingTimeSlotWrapper;
